import { activeCurrency, formatMoneyExact } from "@/lib/format";

// Sdílená logika kontroly faktury (Ekonomika → Doklady i globální „+").
// `ui_type` (přijatá × vydaná) je jen stav dialogu — NEplést s backendovým `doc_type`.

// Pole, která žijí jen v dialogu — do žádného save payloadu nepatří.
const UI_ONLY_FIELDS = ["ui_type", "paid_total", "project_name", "direction", "direction_reason",
  "company_has_ico", "ocr_parties", "customer_name", "customer_ico", "customer_dic", "line_project_ids", "party_from_investor",
  "doc_kind", "doc_title", "confirm_kind"];

// 386: čtení dokladu poznalo papír, který účetní doklad není (objednávka, dodací list…). Do nákladů nepatří —
// server ho bez výslovného potvrzení (`confirm_kind`) neuloží a vrátí 409 `not_accounting_doc`.
export const NOT_ACCOUNTING = { objednavka: "objednávka", potvrzeni_objednavky: "potvrzení objednávky", nabidka: "nabídka",
  dodaci_list: "dodací list", upominka: "upomínka", jine: "jiný dokument" };
export const notAccounting = (d) => !!d && !d.id && !!NOT_ACCOUNTING[d.doc_kind];
export const kindBlocked = (d) => notAccounting(d) && !d.confirm_kind;
export function notAccountingFrom409(err) {
  const d = err?.response?.status === 409 ? err.response.data?.detail : null;
  return d && d.code === "not_accounting_doc" ? d : null;
}

export function stripUiFields(doc) {
  const out = { ...doc };
  UI_ONLY_FIELDS.forEach((k) => delete out[k]);
  return out;
}

// Z výsledku OCR: předvyplní typ podle `direction` a do supplier_* dá PROTISTRANU
// (u vydané je to odběratel — dialog ho tak i popisuje). Obě strany zůstávají
// v `ocr_parties`, aby ruční přepnutí typu mohlo pole prohodit.
export function reviewFieldsFromOcr(data) {
  const issued = data.direction === "vydana";
  return {
    ...data,
    ui_type: issued ? "vydana" : "prijata",
    supplier_name: issued ? data.customer_name || "" : data.supplier_name || "",
    supplier_ico: issued ? data.customer_ico : data.supplier_ico,
    supplier_dic: issued ? data.customer_dic : data.supplier_dic,
    ocr_parties: {
      supplier: { name: data.supplier_name, ico: data.supplier_ico, dic: data.supplier_dic },
      customer: { name: data.customer_name, ico: data.customer_ico, dic: data.customer_dic },
    },
  };
}

// Ruční přepnutí typu u NOVÉHO skenu: prohodí protistranu (vydaná → odběratel,
// přijatá → dodavatel). Uložený doklad nebo ruční zápis bez OCR jen přepne typ.
export function withUiType(doc, v) {
  if (!doc.ocr_parties || doc.id) return { ...doc, ui_type: v };
  const p = (v === "vydana" ? doc.ocr_parties.customer : doc.ocr_parties.supplier) || {};
  return { ...doc, ui_type: v, supplier_name: p.name || "", supplier_ico: p.ico ?? null, supplier_dic: p.dic ?? null, party_from_investor: null };
}

// Hodnota selectu „Typ" pro dialog kontroly / nahrávání (167b/C): 4 druhy faktury.
export function docTypeValue(doc, issued) {
  return doc?.is_credit_note
    ? (issued ? "dobropis_vydany" : "dobropis_prijaty")
    : (issued ? "vydana" : "prijata");
}

// Přepnutí „Typ" na doklad (167b/C): nastaví is_credit_note, u změny přijatá↔vydaná
// prohodí protistranu (withUiType), a při zrušení dobropisu smaže napojení na originál.
// „uctenka" a callback onTypeChange řeší volající dialog (side-effecty tam nepatří).
export function applyDocType(doc, v) {
  const credit = v.startsWith("dobropis");
  const base = (v === "vydana" || v === "dobropis_vydany") ? "vydana" : "prijata";
  const issued = doc.ui_type === "vydana";
  const baseChanged = (base === "vydana") !== issued;
  const next = baseChanged ? withUiType(doc, base) : { ...doc };
  next.is_credit_note = credit;
  if (!credit) { next.credits_document_id = null; next.credits_invoice_id = null; }
  return next;
}

// Kolik se má u dokladu skutečně zaplatit / inkasovat (171): u konečné faktury
// s odečtenými zálohami jen doplatek (`amount_due` vydané počítá server; přijatá konečná
// má odečtené zálohy, 291, nebo ručně opsané „K úhradě“), jinak celá částka.
export function payableTotal(d) {
  if (!d) return 0;
  if (d.amount_due != null && d.amount_due !== "") return Number(d.amount_due) || 0;
  const total = Number(d.total_with_vat) || Number(d.total_without_vat) || 0;
  return Math.max(total - (Number(d.advances_deducted_gross) || 0), 0);
}

// 288: daňový doklad k přijaté platbě — připojí se k záloze, ke které patří (stejné peníze).
export const TAX_DOC = "doklad_k_platbe";

// 291: druh dokladu — malý výběr pod protistranou, stejný pro přijaté i vydané. Dobropis je
// druh dokladu, ne směr (přijatá × vydaná je přepínač nahoře).
export const DOC_KINDS = [
  ["bezna", "Běžná faktura"], ["zaloha", "Zálohová faktura"], [TAX_DOC, "Doklad k platbě"],
  ["konecna", "Konečná faktura"], ["dobropis", "Dobropis"],
];

export function docKind(doc) {
  if (!doc) return "bezna";
  if (doc.is_credit_note) return "dobropis";
  return doc.invoice_type || "bezna";
}

export function applyDocKind(doc, kind) {
  const issued = doc.ui_type === "vydana";
  if (kind === "dobropis") return { ...applyDocType(doc, issued ? "dobropis_vydany" : "dobropis_prijaty"), invoice_type: null };
  const base = doc.is_credit_note ? applyDocType(doc, issued ? "vydana" : "prijata") : { ...doc };
  // 367: K úhradě po zálohách má jen konečná — jinak by skryté pole blokovalo uložení
  return { ...base, invoice_type: kind === "bezna" ? null : kind, ...(kind === "konecna" || issued ? {} : { amount_due: null }) };
}

// Co druh znamená pro peníze — krátká věta vedle výběru.
export function docKindHint(doc) {
  const k = docKind(doc);
  if (doc?.ui_type === "vydana") {
    return { bezna: "Obyčejná faktura za práci nebo dodávku.", zaloha: "Záloha se v tržbách počítá v měsíci zaplacení.",
      [TAX_DOC]: "Daňový doklad k přijaté platbě — připojí se k záloze, nepočítá se podruhé.",
      konecna: "Vyúčtuje zaplacené zálohy a doúčtuje zbytek.", dobropis: "Opravný doklad k vydané faktuře — sníží tržby." }[k];
  }
  return { bezna: "Náklad v plné výši.", zaloha: "Záloha dodavateli není náklad — náklad je až konečná faktura.",
    [TAX_DOC]: "Doklad k zaplacené záloze — připojí se k ní, nepočítá se podruhé.",
    konecna: "Náklad v plné výši, zaplatit zbývá doplatek po zálohách.", dobropis: "Opravný doklad od dodavatele — sníží náklady." }[k];
}

// Odečítá faktura zálohy? (171; přijaté 291) Zálohová ani dobropis nikdy; jinak podle výběru.
export function canDeductAdvances(doc) {
  return !!doc && !doc.is_credit_note && !["zaloha", TAX_DOC].includes(doc.invoice_type);
}

// 321: měna, ve které jsou částky ve formuláři — u neuloženého dokladu v cizí měně ta cizí
// (převod na měnu firmy udělá server při uložení), jinak měna firmy (undefined).
export const pendingCurrency = (doc) => (doc && !doc.foreign && doc.currency && doc.currency.toUpperCase() !== activeCurrency()
  ? doc.currency.toUpperCase() : undefined);
// 324: převedený doklad — částka z papíru („1 000,00 €“) a kurz pro seznam; bez cizí měny null.
export function foreignAmount(d) {
  const f = d?.foreign;
  if (!f) return null;
  const paper = f.total_with_vat ?? f.total ?? f.total_without_vat;
  return {
    text: ["CZK", "EUR"].includes(f.currency) ? formatMoneyExact(paper, f.currency) : `${paper} ${f.currency}`,
    rate: `kurz ${Number(f.rate).toLocaleString("cs-CZ", { maximumFractionDigits: 4 })}${f.date ? ` · ${f.source === "ručně" ? "ručně" : "ČNB"} ${f.date.split("-").reverse().map(Number).join(". ")}` : ""}`,
  };
}
// 321: kurz z formuláře pro API — prázdný nebo neplatný = server vezme kurz ČNB k datu vystavení.
export function fxRateOf(doc) {
  const r = Number(String(doc?.fx_rate ?? "").replace(",", "."));
  return doc?.fx_rate === "" || doc?.fx_rate == null || !(r > 0) ? undefined : r;
}
// Měna z OCR: jen třípísmenný kód, jinak měna firmy.
export const ocrCurrency = (c, fallback) => (/^[A-Za-z]{3}$/.test(c || "") ? c.toUpperCase() : fallback || null);

// Payload pro POST/PATCH /issued-invoices z dialogového stavu.
export function issuedPayloadFromDoc(doc) {
  return {
    invoice_number: doc.invoice_number || "", client_name: doc.supplier_name || "",
    tag: doc.tag || null,                         // 365: štítek vydané faktury
    client_ico: doc.supplier_ico || null, client_dic: doc.supplier_dic || null,
    variable_symbol: doc.variable_symbol || null, project_id: doc.project_id || null,
    issue_date: (doc.issue_date || "").slice(0, 10) || null, due_date: (doc.due_date || "").slice(0, 10) || null,
    total_without_vat: Number(doc.total_without_vat) || 0, vat_amount: Number(doc.vat_amount) || 0,
    total_with_vat: Number(doc.total_with_vat) || 0, file_url: doc.file_url || null, note: doc.note || null,
    payment_status: doc.payment_status || null,   // „Zaplaceno" → backend dorovná platbu (127/B1)
    paid_date: doc.paid_date || null, paid_method: doc.paid_method || null,  // datum+způsob dorovnání (128/B)
    invoice_type: doc.invoice_type || null,       // Záloha/Konečná (129/A)
    // Dobropis vydaný (167) — částky zůstávají kladné, znaménko dá ekonomika.
    is_credit_note: !!doc.is_credit_note,
    credits_invoice_id: doc.credits_invoice_id || null,
    credited_invoice_number: doc.credited_invoice_number || null,
    // Odečtené zálohy (171): undefined = beze změny (JSON pole vynechá), [] = žádné.
    advance_ids: canDeductAdvances(doc) && Array.isArray(doc.advance_ids) ? doc.advance_ids : undefined,
    currency: doc.currency || null, fx_rate: fxRateOf(doc),      // 321: cizí měna → server převede
    confirm_kind: doc.confirm_kind ? true : undefined,            // 386
    // 288: doklad k platbě — ke které záloze patří (bez výběru dohledá server podle čísla nebo částky).
    ...(doc.invoice_type === TAX_DOC ? { for_advance_id: doc.for_advance_id || null, advance_numbers: doc.advance_numbers || [] } : {}),
  };
}

// Payload pro POST /receipts z dialogového stavu (i pro kontrolu duplicit).
// `ocr_raw` / `ocr_kind_switched` (150) jsou jen stav dialogu — sem nepatří.
export function receiptPayload(rec) {
  return {
    file_url: rec.file_url || null, supplier_name: rec.supplier_name, address: rec.address || null,
    receipt_number: (rec.receipt_number || "").trim() || null, date: rec.date || null,
    total: Number(rec.total) || 0, vat_amount: Number(rec.vat_amount) || 0,
    payment_method: rec.payment_method || null, category: rec.category, project_id: rec.project_id || null,
    items: rec.items || [], currency: rec.currency || null, fx_rate: fxRateOf(rec),     // 321
    tag: rec.tag || null,                                                            // 372: štítek nad obchodem
    confirm_kind: rec.confirm_kind ? true : undefined,                               // 386
  };
}

// Endpoint kontroly duplicit podle druhu dokladu (149).
export const DUP_CHECK_URL = { document: "/documents/check-duplicates", receipt: "/receipts/check-duplicates", issued: "/issued-invoices/check-duplicates" };

// Stav dialogu faktury z výsledku OCR (společné pro Ekonomiku i globální „+").
export function invoiceFromOcr(data, url, cats, currency) {
  const items = (data.line_items || []).map((li) => ({ ...li, project_id: null, category: li.category || cats[0] }));
  return { ...reviewFieldsFromOcr(data), doc_type: "faktura", file_url: url, currency: ocrCurrency(data.currency, currency || "CZK"),
    payment_status: "Nezaplaceno", assignment_mode: "whole", project_id: null, category: cats[0], line_items: items };
}

// OCR přes „Nahrát fakturu" řeklo „účtenka" (150) → stav kontroly účtenky.
// `ocr_raw` drží původní data pro „Otevřít jako fakturu".
export function receiptFromInvoiceOcr(data, url) {
  return {
    file_url: url, supplier_name: data.supplier_name || "", address: "",
    receipt_number: data.invoice_number || "", date: (data.issue_date || "").slice(0, 10),
    total: Number(data.total_with_vat) || 0, vat_amount: Number(data.vat_amount) || 0,
    payment_method: "", category: "", project_id: null, ocr_kind_switched: true, ocr_raw: data, currency: ocrCurrency(data.currency),
    doc_kind: data.doc_kind, doc_title: data.doc_title, confirm_kind: data.confirm_kind,      // 386
    items: (data.line_items || []).map((li) => ({ description: li.description || "", quantity: Number(li.quantity) || 0, unit_price: Number(li.unit_price) || 0 })),
  };
}

// Ruční přepnutí „Typ → Účtenka" u čerstvého skenu: bere UPRAVENÁ pole dialogu.
// Stav faktury je nadmnožina OCR dat, proto může posloužit jako `ocr_raw` pro cestu zpět.
export function receiptFromDocState(doc) {
  return receiptFromInvoiceOcr({ ...doc, supplier_name: doc.supplier_name, invoice_number: doc.invoice_number,
    issue_date: doc.issue_date, total_with_vat: doc.total_with_vat, vat_amount: doc.vat_amount, line_items: doc.line_items }, doc.file_url);
}

// 409 s `detail.duplicates` = server odmítl uložit kvůli duplicitě — klient ukáže banner, ne toast.
export function duplicatesFrom409(err) {
  const d = err?.response?.status === 409 ? err.response.data?.detail : null;
  return d && Array.isArray(d.duplicates) ? d.duplicates : null;
}

// Payload pro POST/PATCH /documents (přijatá faktura).
export function receivedPayloadFromDoc(doc) {
  return {
    ...stripUiFields(doc),
    fx_rate: fxRateOf(doc),                       // 321: prázdný kurz = ČNB
    confirm_kind: doc.confirm_kind ? true : undefined,   // 386: „Je to opravdu faktura“
    total_without_vat: Number(doc.total_without_vat) || 0, vat_amount: Number(doc.vat_amount) || 0, total_with_vat: Number(doc.total_with_vat) || 0,
    // 291: druh přijaté faktury, K úhradě u konečné, u dokladu k platbě záloha, ke které patří.
    invoice_type: doc.is_credit_note ? null : doc.invoice_type || null,
    amount_due: doc.invoice_type !== "konecna" || doc.is_credit_note || doc.amount_due === "" || doc.amount_due == null ? null : Number(doc.amount_due),
    advance_numbers: doc.advance_numbers || [],
    for_advance_id: doc.invoice_type === TAX_DOC ? doc.for_advance_id || null : undefined,
    // Odečtené zálohy dodavatele: undefined = beze změny, [] = žádné (jako vydané, 171).
    advance_ids: canDeductAdvances(doc) && Array.isArray(doc.advance_ids) ? doc.advance_ids : undefined,
    // 279: chybějící sazba je „neuvedeno“ (null), ne 0 % — jinak ručně přidaný řádek nesl DPH 0.
    line_items: (doc.line_items || []).map((li) => ({ ...li, quantity: Number(li.quantity) || 0, unit_price: Number(li.unit_price) || 0, line_total: Number(li.line_total) || 0,
      vat_rate: li.vat_rate === "" || li.vat_rate == null || Number.isNaN(Number(li.vat_rate)) ? null : Number(li.vat_rate) })),
  };
}

// 287: položky faktury jako v účetním programu — množství × cena za MJ = celkem bez DPH,
// sazba u každé položky a součty dokladu z položek. DPH se počítá ze základu každé sazby
// (zaokrouhlení až ze součtu, jako v rekapitulaci na faktuře).
const num = (v) => Number(String(v ?? "").replace(",", ".")) || 0;
const r2 = (x) => Math.round(x * 100) / 100;
export const VAT_RATES = [21, 12, 0];
const rateOf = (li) => (li.vat_rate === "" || li.vat_rate == null || Number.isNaN(Number(li.vat_rate)) ? null : Number(li.vat_rate));

export function linesTotals(lines) {
  const byRate = {};
  let net = 0;
  for (const li of lines || []) {
    const n = num(li.line_total);
    net += n;
    const r = rateOf(li);
    if (r != null) byRate[r] = (byRate[r] || 0) + n;
  }
  const vat = Object.entries(byRate).reduce((s, [r, b]) => s + r2((b * Number(r)) / 100), 0);
  return { net: r2(net), vat: r2(vat), gross: r2(net + vat), byRate };
}

// Cena za MJ, která sedí s řádkem: uložená, když dává celkem; jinak celkem / množství
// (čtení faktury často vrátí cenu před slevou).
export function lineUnitPrice(li) {
  const q = num(li.quantity), t = num(li.line_total), p = num(li.unit_price);
  if (q && p && Math.abs(q * p - t) <= Math.max(1, Math.abs(t) * 0.005)) return li.unit_price;
  return q && t ? r2(t / q) : li.unit_price ?? "";
}

// Změna jednoho pole položky: množství nebo cena přepočte celkem, celkem přepočte cenu za MJ.
export function lineWithAmount(li, key, value) {
  if (key === "vat_rate") return { ...li, vat_rate: value === "" || value == null ? null : Number(value) };
  const q = num(li.quantity);
  if (key === "quantity") {
    const p = num(lineUnitPrice(li));
    return p ? { ...li, quantity: value, unit_price: p, line_total: r2(num(value) * p) } : { ...li, quantity: value };
  }
  if (key === "unit_price") return { ...li, unit_price: value, line_total: r2((q || 1) * num(value)) };
  return { ...li, line_total: value, ...(q ? { unit_price: r2(num(value) / q) } : {}) };
}

// Řádek „Nerozděleno“ (auto_rest) dopočítává server z rozdílu dokladu a položek — do součtů položek nepatří.
export const realLines = (lines) => (lines || []).filter((li) => !li.auto_rest);

// Hlavička jde za položkami, dokud s nimi souhlasí (nový doklad, nebo součty sedí do 1 Kč).
// Když se liší (načtená faktura, ručně zadaná celková částka), zůstane — rozdíl ukáže dialog.
export function headerFollowsLines(doc) {
  const n = num(doc.total_without_vat), g = num(doc.total_with_vat);
  if (!n && !g) return true;
  const t = linesTotals(realLines(doc.line_items));
  return Math.abs(n - t.net) <= 1 && Math.abs(g - t.gross) <= 1;
}

export function docWithLines(doc, lines) {
  if (!headerFollowsLines(doc)) return { ...doc, line_items: lines };
  const t = linesTotals(realLines(lines));
  return { ...doc, line_items: lines, total_without_vat: t.net, vat_amount: t.vat, total_with_vat: t.gross };
}

// Patří doklad k zakázce? (166) Celá faktura / účtenka / vydaná podle project_id,
// rozdělená faktura když aspoň jeden její řádek míří na zakázku. Řádek lehkého
// seznamu (220, GET /documents?view=list) nemá line_items, jen line_project_ids.
// Stejné pravidlo drží backend project_docs_filter (routes/documents.py).
export function docOnProject(raw, kind, pid) {
  if (!raw || !pid) return false;
  if (raw.project_id === pid) return true;
  if (kind !== "faktura" || raw.assignment_mode !== "split") return false;
  return Array.isArray(raw.line_items)
    ? raw.line_items.some((li) => li.project_id === pid)
    : (raw.line_project_ids || []).includes(pid);
}

// Řádek lehkého seznamu dokladů (220) — bez položek. Do Kontroly dokladu musí jít
// celý doklad (GET /documents/{id}), jinak by uložení smazalo položky.
export function isLightDoc(d) {
  return !!d && !Array.isArray(d.line_items);
}

// Vydané faktury vidí jen tyto role (backend INVOICE_ROLES v routes/issued.py);
// ostatním by /issued-invoices vrátilo 403 — zbytečný požadavek.
export const INVOICE_ROLES = ["OWNER", "ASSISTANT", "ACCOUNTANT"];
export const canSeeIssued = (user) => !!user && INVOICE_ROLES.includes(user.role);

// Řádek seznamu po změně úhrad (220): endpoint vrací aktualizovaný doklad — řádek se
// srovná na místě, bez přenačtení celého seznamu. Pole, která endpoint nevrací
// (jméno zakázky, line_project_ids, upozornění na zálohy), zůstávají z řádku.
export function mergeUpdatedRow(list, updated) {
  if (!updated?.id) return list;
  return list.map((x) => (x.id === updated.id ? { ...x, ...updated } : x));
}

// 389: bez DPH / DPH / s DPH dokladu pro Doklady (přepínač Zobrazení › Částky). Uložená pole (164),
// u starších dokladů dopočet jako `money_split` na serveru — zná-li se jen s DPH, bez DPH = s DPH a DPH je
// „neuvedeno“ (`unknown`). Účtenka má jen `total` (s DPH) a `vat_amount`.
export function docMoney(d, kind) {
  if (!d) return { net: 0, vat: 0, gross: 0, unknown: false };
  if (d.amount_net != null || d.amount_gross != null) {
    const net = d.amount_net ?? d.amount_gross, gross = d.amount_gross ?? d.amount_net;
    return { net, vat: d.amount_vat ?? r2(gross - net), gross, unknown: !!d.vat_unknown };
  }
  if (kind === "uctenka") {
    const g = Number(d.total) || 0, v = Number(d.vat_amount) || 0;
    return { net: r2(g - v), vat: v, gross: g, unknown: !!g && !v };
  }
  const n0 = d.total_without_vat || null, v0 = d.vat_amount || null, g0 = d.total_with_vat || null;
  if (g0 != null && n0 == null && v0 == null) return { net: g0, vat: 0, gross: g0, unknown: true };
  if (n0 != null && v0 == null && g0 == null) return { net: n0, vat: 0, gross: n0, unknown: false };
  let net = n0, vat = v0, gross = g0;
  if (net == null && gross != null && vat != null) net = r2(gross - vat);
  if (gross == null && net != null && vat != null) gross = r2(net + vat);
  if (vat == null && net != null && gross != null) vat = r2(gross - net);
  net = net ?? gross ?? 0; gross = gross ?? net;
  return { net, vat: vat ?? r2(gross - net), gross, unknown: false };
}
// Podíl rozdělené faktury v zakázce (387): s DPH ze serveru, bez DPH `project_share_net` (389), u starší odpovědi poměrem.
export function shareMoney(d, m) {
  const gross = d.project_share;
  const net = d.project_share_net ?? r2(m.gross ? gross * (m.net / m.gross) : gross);
  return { net, vat: r2(gross - net), gross, unknown: m.unknown };
}
// Která částka se v Dokladech ukazuje: volba v Zobrazení, jinak osobní volba z Nastavení (394, user.amounts_pref),
// jinak podle firmy — plátce bez DPH (DPH se vrací), neplátce s DPH.
export const AMOUNT_MODES = { net: "Bez DPH", gross: "S DPH", both: "Obojí" };
export const companyAmountMode = (company) => (company?.vat_payer === false ? "gross" : "net");
export const defaultAmountMode = (company, user) => (AMOUNT_MODES[user?.amounts_pref] ? user.amounts_pref : companyAmountMode(company));
export const amountOf = (m, mode) => (mode === "gross" ? m.gross : m.net);
